import { createPopup } from '@zelos/shared/lib/create-popup.service';
import { useRefetchDetailUser } from '@zelos/shared/lib/sign.service';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { v5 as uuidv5 } from 'uuid';
import { createIdentityBroadcastChannel } from './create-identity-broadcast-channel';
import { showToast } from '@/lib/toast';

export type IdentityType = 'general' | 'ipin' | 'guardian-general';

type IdentityProgress = 'idle' | 'success-identity' | 'success-profile-update';

type IdentityChannelMessage = {
  type: 'progress',
  payload: IdentityProgress,
} | {
  type: 'error',
  payload: string,
};

type UseIdentityProps = {
  onSuccessCallback?: () => void,
  onErrorCallback?: (message: string) => void,
};

const identityPopupKey = uuidv5(
  'https://certi.programmers.co.kr/certi-itentity-popup',
  uuidv5.URL,
);

export const useVaildIdentityPopup = () => {
  useEffect(() => {
    typeof window !== 'undefined'
    && (window.name !== identityPopupKey || window.parent !== window)
    && (alert('잘못된 접근입니다.'), window.close(), location.replace('about:blank'));
  }, []);
};

const isIdentityProgress = (() => {
  const isIdentityProgressSet = new Set<IdentityProgress>([
    'success-identity',
    'success-profile-update',
  ]) as Set<string>;

  return (value?: string | string[] | null): value is IdentityProgress => typeof value === 'string' && isIdentityProgressSet.has(value);
})();

const identityProgressAtom = atom<IdentityProgress>('idle');

const identityErrorAtom = atom<string | null>(null);

export const useIdentityCallback = () => {
  const [identityProgress, setIdentityProgress] = useAtom(identityProgressAtom);
  const [identityError, setIdentityError] = useAtom(identityErrorAtom);
  const identityChannelRef = useRef<BroadcastChannel | null>(null);
  const refetchDetailUser = useRefetchDetailUser();

  useEffect(() => {
    identityChannelRef.current = createIdentityBroadcastChannel();
    identityChannelRef.current.onmessage = ({ data }: MessageEvent<IdentityChannelMessage>) => {
      data.type === 'progress'
      && isIdentityProgress(data.payload)
      && setIdentityProgress(data.payload);

      data.type === 'error'
      && setIdentityError(data.payload);
    };
    return () => {
      identityChannelRef.current?.close();
      identityChannelRef.current = null;
    };
  }, [setIdentityProgress, setIdentityError]);

  useEffect(() => {
    identityProgress === 'success-identity'
    && refetchDetailUser?.()
      .then(() => {
        setIdentityProgress('success-profile-update');
      }).catch((error) => {
        setIdentityError('profile update error');
        throw error;
      });

    identityProgress === 'success-profile-update'
    && setIdentityProgress('idle');
  }, [
    identityProgress,
    setIdentityProgress,
    setIdentityError,
    refetchDetailUser,
  ]);

  useEffect(() => {
    identityError
    && (setIdentityProgress('idle'), setIdentityError(null));
  }, [
    identityError,
    setIdentityError,
    setIdentityProgress,
  ]);
};

export const useIdentity = ({
  onSuccessCallback,
  onErrorCallback,
}: UseIdentityProps = {}) => {
  const identityProgress = useAtomValue(identityProgressAtom);
  const identityError = useAtomValue(identityErrorAtom);

  const startIdentity = useCallback((type: IdentityType) => {
    if (typeof BroadcastChannel !== 'function') {
      showToast({ text: '본인 인증이 불가능합니다. 다른 브라우저를 이용해 주세요', type: 'error' });
      return;
    }
    if (identityProgress === 'success-identity' || identityProgress === 'success-profile-update') return;

    createPopup({
      url: `/identity-verification/form/${type}`,
      name: identityPopupKey,
    });
  }, [identityProgress]);

  useEffect(() => {
    identityProgress === 'success-profile-update'
    && onSuccessCallback?.();
  }, [identityProgress, onSuccessCallback]);

  useEffect(() => {
    identityError
    && onErrorCallback?.(identityError);
  }, [identityError, onErrorCallback]);

  return {
    startIdentity,
  };
};

export const identityProgressAtomForTest = () => {
  if (process.env.NODE_ENV !== 'test')
    throw new Error('unreachable atom identityProgressAtom');
  return identityProgressAtom;
};
